/* eslint-disable @atlaskit/design-system/no-direct-use-of-web-platform-drag-and-drop */
/* eslint-disable @repo/internal/dom-events/no-unsafe-event-listeners */
/* eslint-disable @atlaskit/platform/no-direct-document-usage */
import type React from 'react';
import {
	useEffect,
	useRef,
	useMemo,
	useCallback,
	type ReactElement,
	type SyntheticEvent,
} from 'react';

export type ChildRenderProps = {
	onMouseDownCapture: (e: SyntheticEvent) => void;
	onTouchEndCapture: (e: SyntheticEvent) => void;
};
type Props = {
	children: (arg1: ChildRenderProps) => ReactElement;
	onClickOutside: (arg1: Event) => void;
};

export const OutsideClickAlerter = ({ onClickOutside, children }: Props): React.ReactElement => {
	const innerClickRef = useRef<boolean>(false);
	const clickStartRef = useRef<boolean>(false);

	const handleClick = useCallback(
		(e: MouseEvent | TouchEvent) => {
			if (clickStartRef.current && !innerClickRef.current && e.target) {
				onClickOutside && onClickOutside(e);
			}
			innerClickRef.current = false;
			clickStartRef.current = false;
		},
		[onClickOutside],
	);

	const innerClick = useCallback(() => {
		innerClickRef.current = true;
	}, []);

	const clickStart = useCallback(() => {
		clickStartRef.current = true;
	}, []);

	useEffect(() => {
		document.addEventListener('mousedown', clickStart, true);
		document.addEventListener('touchstart', clickStart, true);
		document.addEventListener('click', handleClick, true);
		document.addEventListener('touchend', handleClick, true);
		document.addEventListener('drop', handleClick, true);
		document.addEventListener('dragend', handleClick, true);
		return () => {
			document.removeEventListener('mousedown', clickStart, true);
			document.removeEventListener('touchstart', clickStart, true);
			document.removeEventListener('click', handleClick, true);
			document.removeEventListener('touchend', handleClick, true);
			document.removeEventListener('drop', handleClick, true);
			document.removeEventListener('dragend', handleClick, true);
		};
	}, [handleClick, clickStart]);

	const childRenderProps = useMemo(
		() => ({
			onMouseDownCapture: innerClick,
			onTouchEndCapture: innerClick,
		}),
		[innerClick],
	);

	return children(childRenderProps);
};
